import { Box, Popover, Portal, Text } from '@chakra-ui/react';
import { ReactNode, useState } from 'react';
import CrossIcon from 'src/assets/icons/form-elements/CrossIcon';
import TickIcon from 'src/assets/icons/form-elements/TickIcon';
import Input from 'src/components/input';
import { useFormContext } from 'react-hook-form';
import { InputLabelInsideFormControl } from './InputLabelInside';

interface PasswordCriteriaProps {
  test: RegExp;
  value: string;
  children: ReactNode;
}

function PasswordCriteria({ test, value, children }: PasswordCriteriaProps) {
  return (
    <Box>
      {value?.match(test) ? (
        <TickIcon w="11px" h="9px" mx="9px" color="podcastPurple" />
      ) : (
        <CrossIcon w="9px" h="9px" mx="10px" />
      )}
      <Text as="span" size="p3" color="podcastShade400">
        {children}
      </Text>
    </Box>
  );
}

interface PasswordFieldProps {
  disabled: boolean;
}

export default function PasswordField({ disabled }: PasswordFieldProps) {
  const [isPasswordFocussed, setIsPasswordFocussed] = useState(false);

  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext();

  const password = watch('password');

  return (
    <Popover.Root
      open={isPasswordFocussed}
      positioning={{ placement: 'bottom-end' }}
      autoFocus={false}
      visual="light"
    >
      <InputLabelInsideFormControl
        id="password"
        label="Your password:"
        invalid={!!errors.password}
        errorMessage={isPasswordFocussed ? '' : String(errors.password?.message)}
        disabled={disabled}
      >
        <Input
          {...register('password')}
          variation="password"
          placeholder="make up a secure password"
          autoComplete="new-password"
          onBlur={() => {
            setIsPasswordFocussed(false);
          }}
          onFocus={() => setIsPasswordFocussed(true)}
          disabled={disabled}
        />
      </InputLabelInsideFormControl>

      <Popover.Trigger asChild>
        <Box />
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content>
            <Popover.Arrow />
            <Popover.Body>
              <Text fontSize="13px">Your password must include:</Text>
              <PasswordCriteria value={password} test={/.*[a-z].*/}>
                One lowercase letter (a, b, c&hellip;)
              </PasswordCriteria>
              <PasswordCriteria value={password} test={/.*[A-Z].*/}>
                One capital letter (A, B, C&hellip;)
              </PasswordCriteria>
              <PasswordCriteria value={password} test={/.*[0-9].*/}>
                One number (1, 2, 3&hellip;){' '}
              </PasswordCriteria>
              <PasswordCriteria value={password} test={/^.{8,}$/}>
                Minimum 8 characters
              </PasswordCriteria>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
